import * as THREE from 'three';
import { useAppStore } from '../state/store';
import { useBIMStore } from '../state/useBIMStore';
import { useSketchStore } from '../state/useSketchStore';

/**
 * World-space bounding box of the shared document (CAD entities + BIM
 * elements + sketch solids). Extracted from the zoom-chord handler so every
 * viewport consumer (ZoomExtents, quad preview framing) frames the SAME extent.
 *
 * @param ids When non-null, only these entity/element ids are measured
 *            (ZoomToSelection); null measures the whole document.
 * @returns The box, or null when no locatable geometry exists.
 */
export function collectDocumentBounds(ids: string[] | null = null): THREE.Box3 | null {
    const box = new THREE.Box3();
    let found = false;
    const expandPoint = (p: number[] | undefined) => {
        if (!p || p.length < 2) return;
        box.expandByPoint(new THREE.Vector3(p[0], p[1], p[2] ?? 0));
        found = true;
    };
    const entities = useAppStore.getState().entities;
    const elements = useBIMStore.getState().project.elements;
    if (!ids) {
        // All extents: every CAD entity + every BIM element.
        Object.values(entities).forEach((e: any) => {
            const d = e?.data ?? {};
            expandPoint(d.start); expandPoint(d.end); expandPoint(d.center); expandPoint(d.position);
            if (Array.isArray(d.corner1)) { expandPoint(d.corner1); expandPoint(d.corner2); }
            if (Array.isArray(d.vertices)) d.vertices.forEach((v: number[]) => expandPoint(v));
            if (Array.isArray(d.points)) d.points.forEach((v: number[]) => expandPoint(v));
        });
        elements.forEach((el: any) => {
            const g = el?.geometryData ?? el?.geometry;
            if (!g) return;
            expandPoint(g.start); expandPoint(g.end); expandPoint(g.position);
            if (Array.isArray(g.points)) g.points.forEach((v: number[]) => expandPoint(v));
        });
        // Sketch workbench content: solids' face contours + loose edges.
        const sketch = useSketchStore.getState();
        Object.values(sketch.solids).forEach((solid) => {
            if (!solid.visible) return;
            solid.faces.forEach((face) => face.outer.forEach((p) => expandPoint(p)));
        });
        Object.values(sketch.edges).forEach((edge) => {
            expandPoint(edge.a);
            expandPoint(edge.b);
        });
    } else {
        ids.forEach((id) => {
            const d: any = entities[id]?.data ?? (elements.get(id) as any)?.geometryData;
            if (!d) return;
            expandPoint(d.start); expandPoint(d.end); expandPoint(d.center); expandPoint(d.position);
        });
    }
    return found ? box : null;
}
